import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'jiraIssueDeleteModalDialogCancelButton'
	| 'jiraIssueDeleteModalDialogConfirmButton'
	| 'jiraIssueDeleteButtonLabelText'
	| 'jiraIssueDeleteButtonErrorMessageText'
	| 'jiraIssueDeleteModalDialogBodyText'
	| 'jiraIssueDeleteModalDialogTitleText'
	| 'jiraIssueDeleteListTitleText'
	| 'jiraIssueDeleteActionCardTitle';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		jiraIssueDeleteModalDialogCancelButton: {
			id: 'jira-issue-delete.modal-dialog.cancel-button',
			defaultMessage: 'Cancel',
			description:
				'Button label displayed on the cancel button in a confirmation modal dialog when deleting Jira work items within the AI conversation interface. Shown as the text on a button in a modal dialog. When clicked, it closes the confirmation dialog without deleting any work items. The purpose is to provide users with a way to cancel the deletion action if they change their mind.',
		},
		jiraIssueDeleteModalDialogConfirmButton: {
			id: 'jira-issue-delete.modal-dialog.confirm-button',
			defaultMessage: 'Confirm',
			description:
				'Button label displayed on the confirm button in a confirmation modal dialog when deleting Jira work items within the AI conversation interface. Shown as the text on a button in a modal dialog. When clicked, it confirms the deletion and permanently deletes the selected work items. The purpose is to provide users with a clear way to confirm they want to proceed with the irreversible deletion action.',
		},
		jiraIssueDeleteButtonLabelText: {
			id: 'jira-issue-delete.button.label-text',
			defaultMessage: 'Delete selected work items',
			description:
				'Button label displayed on the primary action button in the Jira work item deletion action card within the AI conversation interface. Shown as the text on a button at the bottom of the action card. When clicked, it opens a confirmation modal dialog asking the user to confirm the deletion. The purpose is to provide a clear call-to-action for users to initiate the deletion process for selected work items.',
		},
		jiraIssueDeleteButtonErrorMessageText: {
			id: 'jira-issue-delete.button.error-message-text',
			defaultMessage: 'Select at least one work item to delete',
			description:
				'Error message displayed in the Jira work item deletion action card within the AI conversation interface when a user attempts to delete work items without selecting any items. Shown as an error message or validation text. The purpose is to inform the user that they must select at least one work item before they can proceed with the deletion action.',
		},
		jiraIssueDeleteModalDialogBodyText: {
			id: 'jira-issue-delete.modal-dialog.body-text',
			defaultMessage: `Once you delete a Jira work item, it can't be recovered. Make sure you've carefully checked the work item{s} you're about to delete.`,
			description:
				'Warning message text displayed in the body of a confirmation modal dialog when deleting Jira work items within the AI conversation interface. Shown as the main text in the modal dialog body. The {s} placeholder will be conditionally substituted with "s" when multiple items are selected, or omitted for a single item. The purpose is to warn users that deletion is permanent and irreversible, and to encourage them to carefully review the selected work items before confirming the deletion.',
		},
		jiraIssueDeleteModalDialogTitleText: {
			id: 'jira-issue-delete.modal-dialog.title-text',
			defaultMessage: 'Delete work items',
			description:
				'Title text displayed at the top of a confirmation modal dialog when deleting Jira work items within the AI conversation interface. Shown as the modal dialog header title. The purpose is to immediately communicate to the user what action they are about to confirm, which is permanently deleting work items.',
		},
		jiraIssueDeleteListTitleText: {
			id: 'jira-issue-delete.list-title-text',
			defaultMessage: 'Select all work items',
			description:
				'Title text displayed above a checkbox or selection control in the Jira work item deletion action card within the AI conversation interface. Shown as a section title or label. The purpose is to identify the "select all" control, which allows users to quickly select or deselect all work items in the list at once for bulk deletion.',
		},
		jiraIssueDeleteActionCardTitle: {
			id: 'jira-issue-delete.action-card-title',
			defaultMessage: 'Delete work items',
			description:
				'Title text displayed at the top of an action card in the AI conversation interface when the AI suggests deleting Jira work items. Shown as the card header title. The purpose is to immediately communicate to the user what action the card represents, which is permanently deleting work items from Jira.',
		},
	});

export default message;
